/**
 * Create App page component.
 * Only accessible in preview mode.
 * Apps are sandboxes with the type=app label.
 *
 * @module pages/CreateAppPage
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  Select,
  LabelsEditor,
  WaterfallChart,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { usePreviewMode } from '@/hooks';
import { useCreateApp } from '@/hooks/useApps';
import { connectionService } from '@/services/connectionService';
import type { SandboxRequest, SandboxResponse, ConnectionResponse } from '@/types';
import { ApiException } from '@/services/httpClient';

interface FormState {
  labels: Record<string, string>;
  cpu: string;
  memory: string;
  disk: string;
  selectedConnections: string[];
}

const initialFormState: FormState = {
  labels: {},
  cpu: '1000m',
  memory: '2048Mi',
  disk: '10Gi',
  selectedConnections: [],
};

/**
 * Create app page
 */
export function CreateAppPage() {
  const navigate = useNavigate();
  const createMutation = useCreateApp();

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  // Connections state
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const isPreviewMode = usePreviewMode();

  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [showResults, setShowResults] = useState(false);
  const [createdApp, setCreatedApp] = useState<SandboxResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);
  const [creationError, setCreationError] = useState<{
    message: string;
    detail?: string;
    traceId?: string;
    requestId?: string;
    status?: number;
  } | null>(null);

  // Fetch available connections (only Ready ones) - only in preview mode
  useEffect(() => {
    if (!isPreviewMode) {
      setConnectionsLoading(false);
      return;
    }
    const fetchConnections = async () => {
      try {
        const allConnections = await connectionService.list();
        // Only show Ready connections
        setConnections(allConnections.filter((c) => c.state === 'Ready'));
      } catch (err) {
        console.error('Failed to load connections:', err);
      } finally {
        setConnectionsLoading(false);
      }
    };
    fetchConnections();
  }, [isPreviewMode]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};

    if (!form.cpu.trim()) {
      newErrors.cpu = 'CPU is required';
    }

    if (!form.memory.trim()) {
      newErrors.memory = 'Memory is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    // Note: The type=app label is added automatically by appService.create()
    const request: SandboxRequest = {
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
      vmmType: 'CloudHypervisor',
      entrypoint: ['/bin/sleep'],
      cmd: ['infinity'],
      sourcesRef: {
        diskImage: {
          name: 'ubuntu',
          isPublic: true,
        },
      },
      resources: {
        cpu: form.cpu.trim(),
        memory: form.memory.trim(),
        disk: form.disk.trim() || undefined,
      },
      connections: form.selectedConnections.length > 0 ? form.selectedConnections : undefined,
    };

    const startTime = performance.now();
    setCreationError(null); // Clear any previous error
    createMutation.mutate(request, {
      onSuccess: (data) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));
        setCreatedApp(data);
        setShowResults(true);
      },
      onError: (error) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));

        if (error instanceof ApiException) {
          setCreationError({
            message: error.message,
            detail: error.detail,
            traceId: error.traceId,
            requestId: error.requestId,
            status: error.status,
          });
        } else {
          setCreationError({
            message: error.message || 'An unexpected error occurred',
          });
        }
        setShowResults(true);
      },
    });
  };

  const handleViewApp = () => {
    if (createdApp) {
      navigate(`/apps/${encodeURIComponent(createdApp.id)}`);
    }
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setCreatedApp(null);
    setTimeTakenMs(0);
    setCreationError(null);
    setForm(initialFormState);
  };

  // If showing results, display the results page (success or error)
  if (showResults) {
    // Error case
    if (creationError) {
      return (
        <div>
          <PageHeader
            title="App Creation Failed"
            description="There was an error creating your app"
            breadcrumbs={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Apps', href: '/apps' },
              { label: 'Create' },
            ]}
          />

          <div className="space-y-6">
            <Card>
              <CardHeader title="Error Summary" description="Details about the failure" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="text-sm text-red-600 dark:text-red-400">Status</div>
                    <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                      Failed {creationError.status && `(${creationError.status})`}
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Time Taken</div>
                    <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                      {timeTakenMs.toLocaleString()} ms
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Error
                    </div>
                    <div className="text-red-600 dark:text-red-400 font-medium">
                      {creationError.message}
                    </div>
                  </div>

                  {creationError.detail && (
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Detail
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm">
                        {creationError.detail}
                      </div>
                    </div>
                  )}

                  {(creationError.traceId || creationError.requestId) && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Diagnostic Information
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {creationError.traceId && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Trace ID</div>
                            <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block mt-1 break-all">
                              {creationError.traceId}
                            </code>
                          </div>
                        )}
                        {creationError.requestId && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Request ID
                            </div>
                            <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block mt-1 break-all">
                              {creationError.requestId}
                            </code>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Include these IDs when reporting issues to help with troubleshooting.
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardFooter>
                <div className="flex justify-end gap-3">
                  <Link to="/apps">
                    <Button variant="secondary">Back to Apps</Button>
                  </Link>
                  <Button onClick={handleCreateAnother}>Try Again</Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      );
    }

    // Success case
    if (createdApp) {
      return (
        <div>
          <PageHeader
            title="App Created"
            description="Your app has been created successfully"
            breadcrumbs={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Apps', href: '/apps' },
              { label: 'Create' },
            ]}
          />

          <div className="space-y-6">
            <Card>
              <CardHeader title="Creation Summary" description="App creation details" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-sm text-green-700 dark:text-green-400">Status</div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                      Success
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm text-blue-700 dark:text-blue-400">Time Taken</div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {timeTakenMs.toLocaleString()} ms
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Waterfall Chart - shown when traces are available */}
            {createdApp.debug?.traces && createdApp.debug.traces.length > 0 && (
              <Card>
                <CardHeader
                  title="Performance Waterfall"
                  description="OpenTelemetry-style trace visualization showing timing across all services"
                />
                <CardBody>
                  <WaterfallChart
                    traces={createdApp.debug.traces}
                    traceId={createdApp.debug.traceId}
                    clientDurationMs={timeTakenMs}
                  />
                </CardBody>
              </Card>
            )}

            {/* Performance Timeline - only shown if debug info is present */}
            <Card>
              <CardHeader title="App Details" description="Details of the created app" />
              <CardBody>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">ID</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdApp.id}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Labels</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdApp.labels && Object.keys(createdApp.labels).length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(createdApp.labels as Record<string, string>).map(
                            ([key, value]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              >
                                {key}: {value}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">VMM Type</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdApp.vmmType || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Resources</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      CPU: {createdApp.resources?.cpu || 'N/A'}, Memory:{' '}
                      {createdApp.resources?.memory || 'N/A'}
                      {createdApp.resources?.disk && `, Disk: ${createdApp.resources.disk}`}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardFooter>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={handleCreateAnother}>
                    Create Another
                  </Button>
                  <Button onClick={handleViewApp}>View App</Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      );
    }
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title="Create App"
        description="Create a new app using the base Ubuntu image"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Apps', href: '/apps' },
          { label: 'Create' },
        ]}
        action={
          <Link to="/apps">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card className="lg:col-span-2">
            <CardHeader title="Basic Information" description="Provide basic details for the app" />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <LabelsEditor
                    ref={labelsEditorRef}
                    value={form.labels}
                    onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                    helperText="Add key-value pairs to label this app (type=app will be added automatically)"
                  />
                </div>
                {/* Connections dropdown - only in preview mode */}
                {isPreviewMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Connections (optional)
                    </label>
                    <Select
                      options={connections.map((conn) => ({
                        value: conn.id,
                        label: `${conn.name} (${conn.type})`,
                      }))}
                      value={form.selectedConnections[0] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          // Add to selection if not already selected
                          if (!form.selectedConnections.includes(value)) {
                            setForm((prev) => ({
                              ...prev,
                              selectedConnections: [...prev.selectedConnections, value],
                            }));
                          }
                        }
                      }}
                      placeholder={
                        connectionsLoading
                          ? 'Loading connections...'
                          : connections.length === 0
                            ? 'No connections available'
                            : 'Select a connection to add'
                      }
                      disabled={connectionsLoading || connections.length === 0}
                    />
                    {form.selectedConnections.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {form.selectedConnections.map((connId) => {
                          const conn = connections.find((c) => c.id === connId);
                          return (
                            <span
                              key={connId}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-sm"
                            >
                              {conn ? `${conn.name} (${conn.type})` : connId}
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    selectedConnections: prev.selectedConnections.filter(
                                      (id) => id !== connId
                                    ),
                                  }))
                                }
                                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Select connections to make available in the app
                    </p>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Resources */}
          <Card className="lg:col-span-2">
            <CardHeader title="Resources" description="Configure the resources for the app" />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="CPU"
                  placeholder="2000m"
                  value={form.cpu}
                  onChange={(e) => handleChange('cpu', e.target.value)}
                  error={errors.cpu}
                  helperText="CPU in millicores (e.g., 2000m = 2 cores)"
                  required
                />
                <Input
                  label="Memory"
                  placeholder="4Gi"
                  value={form.memory}
                  onChange={(e) => handleChange('memory', e.target.value)}
                  error={errors.memory}
                  helperText="Memory in Gi or Mi (e.g., 4Gi)"
                  required
                />
                <Input
                  label="Disk"
                  placeholder="10Gi"
                  value={form.disk}
                  onChange={(e) => handleChange('disk', e.target.value)}
                  helperText="Disk size in Gi (e.g., 10Gi)"
                />
              </div>
            </CardBody>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/apps">
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={createMutation.isPending}
                >
                  Create App
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
